import {useEffect, useState, useSyncExternalStore} from "react";
import {useTranslation} from "react-i18next";
import {ApiError} from "../../../shared/api/client";
import {sessionStore} from "../../../shared/session/session-store";
import {getAccountDeletionStatus} from "../api-account-deletion";
import {deletionFlow} from "../deletion-flow";
import type {AccountDeletionResult} from "../model";
import {DeletionBlockers} from "./delete-account-page";

export default function AccountDeletionRecoveryPage() {
  const {t} = useTranslation();
  const attempt = useSyncExternalStore(deletionFlow.subscribe,deletionFlow.snapshot);
  const session = useSyncExternalStore(sessionStore.subscribe,sessionStore.snapshot);
  const [result,setResult] = useState<AccountDeletionResult | undefined>();
  const [error,setError] = useState<unknown>();
  const [checking,setChecking] = useState(false);
  const [revision,setRevision] = useState(0);
  useEffect(() => {
    if (sessionStore.snapshot().status === "loading") void sessionStore.bootstrap();
  },[]);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const knownFailure = attempt?.error instanceof ApiError && [409,422].includes(attempt.error.status);
    const poll = async () => {
      if (attempt?.result && attempt.result.state !== "processing") {
        setResult(attempt.result); return;
      }
      if (knownFailure) { setError(attempt.error); return; }
      setChecking(true);
      try {
        const next = await getAccountDeletionStatus(controller.signal);
        if (controller.signal.aborted) return;
        if (attempt && next.operationId !== attempt.operationId) throw new ApiError(404);
        deletionFlow.accept(next);
        setResult(next); setError(undefined);
        if (next.state === "processing") timer = setTimeout(() => { void poll(); },10000);
      } catch (failure) {
        if (controller.signal.aborted) return;
        setError(failure);
        if (attempt?.sending || !(failure instanceof ApiError) || failure.status === 503 || failure.status === 429) timer = setTimeout(() => { void poll(); },10000);
      } finally { if (!controller.signal.aborted) setChecking(false); }
    };
    // A command starts before SPA navigation. Allow it to finish; if the reply
    // stalls, capability recovery can observe commit without resending contact.
    if (attempt?.sending) timer = setTimeout(() => { void poll(); },30000);
    else void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  },[attempt,revision]);
  const completed = result?.state === "anonymized";
  const invalid = error instanceof ApiError && [409,422].includes(error.status);
  const missing = error instanceof ApiError && error.status === 404 && !attempt?.sending;
  const heading = completed ? "completedTitle" : result?.state === "blocked" ? "blockedTitle" : result?.state === "retryable" ? "retryableTitle" : invalid ? "invalidTitle" : missing ? "missingTitle" : "processingTitle";
  return <main className="standalone-auth deletion-recovery">
    <a className="brand-link" href="/">{t("common.brand")}</a>
    <section className="cabinet-panel" aria-labelledby="deletion-recovery-title">
      <p className="eyebrow">{t("cabinet.delete.eyebrow")}</p>
      <h1 id="deletion-recovery-title">{t(`cabinet.delete.${heading}`)}</h1>
      <div role="status" aria-live="polite"><p>{t(`cabinet.delete.${completed ? "completedBody" : invalid ? "invalidBody" : missing ? "missingBody" : result?.state === "blocked" ? "blockedBody" : result?.state === "retryable" ? "retryableBody" : "processingBody"}`)}</p></div>
      {result?.state === "blocked" && <DeletionBlockers blockers={result.blockers} actions={result.nextActions} fullNavigation onRetry={() => window.location.assign("/app/account/delete")} />}
      {!completed && !attempt?.sending && result?.state !== "blocked" && <button type="button" className="button secondary" disabled={checking} onClick={() => setRevision((value) => value+1)}>{t("cabinet.delete.checkStatus")}</button>}
      <div className="dialog-actions"><a className="button secondary" href={session.status === "authenticated" ? "/app" : "/signin"}>{t(session.status === "authenticated" ? "cabinet.delete.returnCabinet" : "cabinet.delete.signIn")}</a><a href="/">{t("cabinet.delete.returnHome")}</a></div>
    </section>
    <section className="cabinet-panel"><h2>{t("cabinet.delete.rulesTitle")}</h2><p>{t("cabinet.delete.warningBody")}</p><ul><li>{t("cabinet.delete.effects.profile")}</li><li>{t("cabinet.delete.effects.history")}</li><li>{t("cabinet.delete.effects.sessions")}</li><li>{t("cabinet.delete.effects.kyc")}</li></ul></section>
  </main>;
}
